import { useCallback, useEffect, useRef, useState } from 'react';
import useDemandStudioEditActions from './useDemandStudioEditActions';
import {
  enrichPlanWithDerivedTotals,
  getLifecarePlanStructuredFieldPath,
  getLifecarePlanTotalCostFieldPath,
} from '../utils/lifecarePlanUtils';

import {
 AUTOSAVE_STATUS
} from "@demandproai/shared";

// autosave for the structured plan. Mirrors the demand studio: manual edits persist as a
// MANUAL_EDIT create request (snapshot), not to the live case. We send the FULL structured plan per
// save so the server has the complete object; the positional field path targets the per-client element.
const useLifecarePlanAutosave = ({ caseId, clientId, onSaveSuccess }) => {
  const { sendEditAction, flushEditAction } = useDemandStudioEditActions({ caseId });
  const [autosaveStatus, setAutosaveStatus] = useState(AUTOSAVE_STATUS.IDLE);
  const lastSavedAtRef = useRef(null);

  const triggerAutosave = useCallback(
    async ({ plan }) => {
      if (!caseId || !clientId) return;
      const enrichedPlan = enrichPlanWithDerivedTotals(plan);
      const fieldPath = getLifecarePlanStructuredFieldPath(clientId);
      const totalCostPath = getLifecarePlanTotalCostFieldPath(clientId);
      const totalCostValue = Number.isFinite(enrichedPlan?.totalLifetimeCost) ? enrichedPlan.totalLifetimeCost : 0;
      setAutosaveStatus(AUTOSAVE_STATUS.SAVING);
      try {
        const response = await sendEditAction({ [fieldPath]: enrichedPlan, [totalCostPath]: totalCostValue });
        if (response?.success === false) {
          setAutosaveStatus(AUTOSAVE_STATUS.FAILED);
          return;
        }
        lastSavedAtRef.current = new Date();
        setAutosaveStatus(AUTOSAVE_STATUS.SAVED);
        onSaveSuccess?.({ plan: enrichedPlan });
      } catch {
        setAutosaveStatus(AUTOSAVE_STATUS.FAILED);
      }
    },
    [caseId, clientId, sendEditAction, onSaveSuccess]
  );

  useEffect(() => {
    setAutosaveStatus(AUTOSAVE_STATUS.IDLE);
    lastSavedAtRef.current = null;
  }, [clientId]);

  return {
    autosaveStatus,
    triggerAutosave,
    flushAutosave: flushEditAction,
    lastSavedAt: lastSavedAtRef.current,
  };
};

export default useLifecarePlanAutosave;
